<template>
  <Layout padding-horizontal="0" padding-vertical="0">
    <UserHeader
        :is-loading="isLoading"
        :title="details.name + ' ' + details.surname"
        :status="details.status"
        :session-count="details.onlineSessionCount"
        :program-count="details.programsAssignedCount"
        :created-at="details.createdAt"
        :photo="details.avatarUrl"
        :on-edit="showEditModal"
        :on-delete="showDeleteModal"
    />
    <div class="main-content">
      <div class="tab-panel-row">
        <div class="tab-panel">
          <button class="tab-button" :class="{'active': tabIndex === 0}" @click="() => changeTab(0)">
            <span>{{ $t('general_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 1}" @click="() => changeTab(1)">
            <span>{{ $t('programs_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 2}" @click="() => changeTab(2)">
            <span>{{ $t('tests_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 5}" @click="() => changeTab(5)">
            <span>{{ $t('game_labs_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 6}" @click="() => changeTab(6)">
            <span>{{ $t('onboarding_title') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 7}" @click="() => changeTab(7)">
            <span>{{ $t('agents_tab') }}</span>
          </button>
        </div>

        <div class="tab-actions">
          <div class="action-row" v-if="tabIndex === 1">
            <button
                :class="deleteProgramSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleProgramDelete"
                :disabled="selectedPrograms.length === 0"
            >
              {{ deleteProgramSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedPrograms.length > 0">({{ selectedPrograms.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="openAssignModal">{{ $t('assign_new') }}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 2">
            <button
                :class="deleteTestSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleTestDelete"
                :disabled="selectedTests.length === 0"
            >
              {{ deleteTestSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedTests.length > 0">({{ selectedTests.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="openAssignTestModal">{{ $t('assign_new') }}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 5">
            <button
                :class="deleteGameLabSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleGameLabDelete"
                :disabled="selectedGameLabs.length === 0"
            >
              {{ deleteGameLabSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedGameLabs.length > 0">({{ selectedGameLabs.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="showAssignGameLabsModal">{{$t('assign_new')}}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 7">
            <button class="dark-button-sm" @click="showAssignAgentModal">{{$t('assign_new')}}</button>
          </div>
        </div>
      </div>

      <div v-if="tabIndex === 0" class="tab-container">
        <h3>{{ $t('user_details') }}</h3>
        <div class="company-details">
          <div class="company-details-col">

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/avatar.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('full_name') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{ details.name }} {{details.surname}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/message.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('email_address') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{ details.email }}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/buildings.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title" v-if="!isLoading">
                  {{ $t('company_name') }}
                </span>
                <span class="detail-text">
                  {{ details.companyName }}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>
          </div>

          <div class="company-details-col">
            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/phone.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('phone_number_person') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{ details.phone }}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/cake.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('birthday') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{  details.birthday || '-' }}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/departments.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('department') }}
                </span>
                <span class="detail-text" v-if="!isLoading" v-for="departments in details?.departments">
                  {{ departments.name }}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div v-if="tabIndex === 1" class="tab-container">
        <h3>{{ $t('assigned_programs') }}</h3>

        <div v-if="!programsLoading && programList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{$t('not_assigned_programs_yet')}}</span>
          <span class="no-tests-description">{{$t('first_program_assign')}}</span>
        </div>

        <div class="program-page-container" v-else>
          <div class="programs-row" v-if="programsLoading">
            <ProgramCard :is-loading="true" />
            <ProgramCard :is-loading="true" />
            <ProgramCard :is-loading="true" />
          </div>

          <div class="programs-row" v-else>
            <ProgramCard
                v-for="program in programList"
                :key="program.programId"
                :image="program.avatar"
                :is-loading="false"
                :title="program.name"
                :description="program.shortDescription"
                :tags="program.tags.map((x) => x.name)"
                :remaining-days="program.remainingDay"
                :assigned-at="program.assignedAt"
                :remaining="program.remaining"
                :start-at="program.startAt"
                :finish-at="program.finishAt"
                :is-publish="program.isPublish"
                :show-delete="true"
                :companies-assigned-count="program.companiesAssignedCount"
                :user-assigned-count="program.userAssignedCount"
                :is-selected="isProgramSelected(program.programId)"
                :on-select="() => handleProgramSelect(program.programId)"
                :progress="program.progress"
                :show-progress="true"
            />
          </div>

          <div class="bottom-placement-pagination">
            <Pagination
                :current-page="programsCurrentPage"
                :total-items="totalPrograms"
                :per-page="programsPerPage"
                :on-page-change="handleProgramsPage"
            />
          </div>
        </div>
      </div>

      <div v-if="tabIndex === 2" class="tab-container">
        <h3>{{ $t('assigned_tests') }}</h3>

        <div v-if="!testsLoading && testList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{$t('not_assigned_tests_yet')}}</span>
          <span class="no-tests-description">{{ $t('first_test_assign') }}</span>
        </div>

        <div class="program-page-container" v-else>
          <div class="tests-grid">
            <free-style-shimmer
                :is-loading="testsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="testsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="testsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <div v-for="item in testList" v-if="!testsLoading" :key="item.testId" class="test-card-wrapper">
              <div class="test-card no-text-select" :class="{'selected': isTestSelected(item.testId)}" @click="handleTestCardClick(item)">

                <div class="checkbox-wrapper" @click.stop="handleTestSelect(item.testId)">
                  <img
                      v-if="!isTestSelected(item.testId)"
                      src="@/assets/images/checkbox-circle.svg"
                      alt="Select"
                      class="checkbox-icon"
                  />
                  <img
                      v-else
                      src="@/assets/images/checkboxchecked-circle.svg"
                      alt="Selected"
                      class="checkbox-icon"
                  />
                </div>

                <div class="status-tag" :class="item.isPublish ? 'Published' : 'Draft'">
                  <div class="status-dot" :class="item.isPublish ? 'Published' : 'Draft'"></div>
                  <span>{{item.isPublish ? $t('published') : $t('draft')}}</span>
                </div>

                <span class="test-date">{{ formatDate(item.created_at) }}</span>
                <span class="test-title">{{item.name}}</span>

                <div class="test-info">
                  <template v-if="item.startAt || item.finishAt">
                    <img src="@/assets/images/datepicker.svg" class="test-info-icon"/>
                    <span class="test-info-text">
                      {{ formatDate(item.startAt) || '-' }} - {{ formatDate(item.finishAt) || '-' }}
                    </span>
                    <div class="dot"></div>
                  </template>

                  <div class="test-time-row" v-if="item.finishAt && item.remaining && item.remaining.isPast === 0">
                    <img
                        src="@/assets/images/clockOutlineRed.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text red">
                      {{ item.remaining.time }}
                      {{$t('time_is_up')}}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 1">
                    <img
                        src="@/assets/images/clockOutlineGreen.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text green">
                      {{ item.remaining.time }}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 2">
                    <img src="@/assets/images/clockOutlineYellow.svg" class="test-info-icon" />
                    <span class="test-info-text yellow">
                      {{ item.remaining.time }}
                    </span>
                  </div>
                </div>

                <div class="info-row">
                  <span class="info-tag">{{$t('companies_count', {companiesCount: item.compainesCount})}}</span>
                  <span class="info-tag">{{$t('question_count', {questionCount: item.questionsCount})}}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bottom-placement-pagination">
            <Pagination
                :current-page="testsCurrentPage"
                :total-items="totalTests"
                :per-page="testsPerPage"
                :on-page-change="handleTestsPage"
            />
          </div>
        </div>
      </div>

      <div v-if="tabIndex === 5" class="tab-container">
        <h3>{{ $t('assigned_game_labs') }}</h3>

        <div v-if="!gameLabsLoading && gameLabsList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{ $t('not_assigned_game_labs_yet') }}</span>
          <span class="no-tests-description">{{ $t('click_on_ata') }}</span>
        </div>

        <div class="program-page-container" v-else>
          <div class="gameLabs-grid">
            <free-style-shimmer
                :is-loading="gameLabsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="gameLabsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="gameLabsLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <div v-for="item in gameLabsList" v-if="!gameLabsLoading" :key="item.gameLabsId" class="gamelab-card-wrapper">
              <div class="test-card no-text-select" :class="{'selected': isGameLabSelected(item.gameLabsId)}" @click="handleGameLabCardClick(item)">
                <div class="checkbox-wrapper" @click.stop="handleGameLabSelect(item.gameLabsId)">
                  <img
                      v-if="!isGameLabSelected(item.gameLabsId)"
                      src="@/assets/images/checkbox-circle.svg"
                      alt="Select"
                      class="checkbox-icon"
                  />
                  <img
                      v-else
                      src="@/assets/images/checkboxchecked-circle.svg"
                      alt="Selected"
                      class="checkbox-icon"
                  />
                </div>

                <div class="status-tag" :class="item.isPublish ? 'Published' : 'Draft'">
                  <div class="status-dot" :class="item.isPublish ? 'Published' : 'Draft'"></div>
                  <span>{{item.isPublish ? $t('published') : $t('draft')}}</span>
                </div>

                <span class="test-date">{{formatDate(item.created_at)}}</span>
                <span class="test-title">{{item.name}}</span>

                <div class="test-info">
                  <template v-if="item.startAt || item.finishAt">
                    <img src="@/assets/images/datepicker.svg" class="test-info-icon"/>
                    <span class="test-info-text">
                      {{ formatDate(item.startAt) || '-' }} - {{ formatDate(item.finishAt) || '-' }}
                    </span>
                    <div class="dot"></div>
                  </template>

                  <div class="test-time-row" v-if="item.finishAt && item.remaining && item.remaining.isPast === 0">
                    <img
                        src="@/assets/images/clockOutlineRed.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text red">
                      {{ item.remaining.time }}
                      {{$t('time_is_up')}}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 1">
                    <img
                        src="@/assets/images/clockOutlineGreen.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text green">
                      {{ item.remaining.time }}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 2">
                    <img
                        src="@/assets/images/clockOutlineYellow.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text yellow">
                      {{ item.remaining.time }}
                    </span>
                  </div>
                </div>

                <div class="info-row">
                </div>
              </div>
            </div>
          </div>

          <div class="bottom-placement-pagination">
            <Pagination
                :current-page="gameLabsCurrentPage"
                :total-items="totalGameLabs"
                :per-page="gameLabsPerPage"
                :on-page-change="handleGameLabsPage"
            />
          </div>
        </div>
      </div>

      <OnboardingTab
          v-if="tabIndex === 6"
          :data="onboardingData"
          :loading="onboardingLoading"
      />

      <div v-if="tabIndex === 7" class="tab-container">
        <h3>{{ $t('assigned_agents') }}</h3>

        <div v-if="!agentsLoading && agentList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{ $t('not_assigned_agents_yet') }}</span>
          <span class="no-tests-description">{{ $t('first_agent_assign') }}</span>
        </div>

        <div class="program-page-container" v-else>
          <div class="agents-grid">
            <free-style-shimmer
                v-if="agentsLoading"
                :is-loading="true"
                height="120px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />
            <free-style-shimmer
                v-if="agentsLoading"
                :is-loading="true"
                height="120px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />
            <free-style-shimmer
                v-if="agentsLoading"
                :is-loading="true"
                height="120px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <div v-for="item in agentList" v-if="!agentsLoading" :key="item.id" class="agent-card-wrapper">
              <div class="agent-card no-text-select">
                <div class="agent-card-top">
                  <img v-if="item.avatarUrl" :src="item.avatarUrl" class="agent-card-avatar" />
                  <div v-else class="agent-card-avatar-placeholder">AI</div>
                  <div class="agent-card-info">
                    <span class="agent-card-name">{{ getAgentName(item.name) }}</span>
                    <span class="agent-card-desc">{{ item.description || '-' }}</span>
                  </div>
                </div>
                <div class="agent-card-bottom">
                  <div class="status-tag" :class="item.status">
                    <div class="status-dot" :class="item.status"></div>
                    <span>{{ item.status }}</span>
                  </div>
                  <span class="agent-card-date">{{ formatDate(item.createdAt) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bottom-placement-pagination">
            <Pagination
                :current-page="agentsCurrentPage"
                :total-items="totalAgents"
                :per-page="agentsPerPage"
                :on-page-change="handleAgentsPage"
            />
          </div>
        </div>
      </div>

    </div>

    <UserEditModal
        :edit-data="details"
        :visible="editModalVisible"
        :on-hide="hideEditModal"
        :refresh-callback="getDetails"
    />

    <ConfirmDeleteModal
        :title="this.$t('alert_messages')"
        :message="this.$t('alert_messages_two')"
        :is-visible="deleteModalVisible"
        :on-action="handleDelete"
        :on-close="handleDeleteModalClose"
    />

    <AssignProgramModal
        :visible="assignmentModal"
        :pre-data="details"
        type="user"
        :on-hide="hideAssignModal"
        :refresh-callback="getProgramsPaginated"
    />

    <AssignTestModal
        :visible="assignTestModal"
        :pre-data="details"
        type="user"
        :on-hide="hideAssignTestModal"
        :refresh-callback="getTestsPaginated"
    />

    <AssignGameLabsModal
        :visible="assignGameLabsModalVisible"
        :pre-data="details"
        type="user"
        :on-hide="hideAssignGameLabsModal"
        :refresh-callback="getGameLabsPaginated"
    />

    <AssignAgentModal
        :visible="assignAgentModalVisible"
        :company-id="details.companyId"
        :user-id="details.userId"
        type="user"
        :on-hide="hideAssignAgentModal"
        :refresh-callback="getAgentsPaginated"
    />

  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import CompanyHeader from "@/components/CompanyHeader.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import UserTable from "@/components/tables/User/UserTable.vue";
import ProgramCard from "@/components/ProgramCard.vue";
import TransactionTable from "@/components/tables/TransactionTable.vue";
import AddUserToCompanyModal from "@/components/modals/InviteUserModal.vue";
import UserModal from "@/components/modals/UserModal.vue";
import UserEditModal from "@/components/modals/UserEditModal.vue";
import userService from "@/service/userService";
import UserHeader from "@/components/UserHeader.vue";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import AssignProgramModal from "@/components/modals/AssignProgramModal.vue";
import AssignTestModal from "@/components/modals/AssignTestModal.vue";
import AddUserModal from "@/components/modals/UserModal.vue";
import {handleErrorResponse} from "@/utils/utils";
import AssignGameLabsModal from "@/components/modals/AssignGameLabsModal.vue";
import AssignAgentModal from "@/components/modals/AssignAgentModal.vue";
import roleService from "@/service/roleService";
import programService from "@/service/programService";
import testService from "@/service/testService";
import gameLabService from "@/service/gameLabService";
import agentService from "@/service/agentService";
import Pagination from "@/components/Pagination.vue";
import OnboardingTab from "@/components/OnboardingTab.vue";

export default {
  components: {
    Pagination,
    AssignGameLabsModal,
    AssignAgentModal,
    UserEditModal,
    AddUserModal,
    AssignTestModal,
    AssignProgramModal,
    ConfirmDeleteModal,
    UserHeader,
    UserModal,
    AddUserToCompanyModal,
    TransactionTable,
    ProgramCard,
    UserTable,
    ExpandableSearch,
    CompanyHeader,
    Layout,
    OnboardingTab
  },
  data() {
    return {
      searchText: '',
      tabIndex: 0,
      editModalVisible: false,
      deleteModalVisible: false,
      addUserModal: false,
      assignmentModal: false,
      isLoading: true,
      details: {},
      assignTestModal: false,
      roleList: [],
      assignGameLabsModalVisible: false,
      selectedPrograms: [],
      selectedTests: [],
      selectedGameLabs: [],
      deleteProgramSure: false,
      deleteTestSure: false,
      deleteGameLabSure: false,
      deleteProgramTimer: null,
      deleteTestTimer: null,
      deleteGameLabTimer: null,
      programsCurrentPage: 1,
      testsCurrentPage: 1,
      gameLabsCurrentPage: 1,
      totalPrograms: 0,
      totalTests: 0,
      totalGameLabs: 0,
      programsPerPage: 9,
      testsPerPage: 9,
      gameLabsPerPage: 9,
      programsLoading: false,
      testsLoading: false,
      gameLabsLoading: false,
      searchTimeout: null,
      programList: [],
      testList: [],
      gameLabsList: [],
      onboardingData: null,
      onboardingLoading: false,
      assignAgentModalVisible: false,
      agentsLoading: false,
      agentList: [],
      agentsCurrentPage: 1,
      totalAgents: 0,
      agentsPerPage: 9,
    }
  },
  mounted() {
    this.getDetails();

    const user = JSON.parse(localStorage.getItem('user'));
    roleService.getStoreByCompanyRole(user.companyId).then(response => {
      this.roleList = response.data.data;
    }).catch(err => {
      handleErrorResponse(err, this.$snackbar);
    });

  },
  methods: {
    handleSearch(e) {
      this.searchText = e?.target?.value || undefined;

      if (this.tabIndex === 1) {
        this.programsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getProgramsPaginated();
        }, 500);
      }

      if (this.tabIndex === 2) {
        this.testsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getTestsPaginated();
        }, 500);
      }

      if (this.tabIndex === 5) {
        this.gameLabsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getGameLabsPaginated();
        }, 500);
      }
    },
    changeTab(index) {
      if (this.isLoading) return;

      this.tabIndex = index;
      this.searchText = '';

      if (index === 1) {
        this.programsCurrentPage = 1;
        this.programsLoading = false;
        this.isLoading = false;
        this.getProgramsPaginated();
      }

      if (index === 2) {
        this.testsCurrentPage = 1;
        this.testsLoading = false;
        this.isLoading = false;
        this.getTestsPaginated();
      }

      if (index === 5) {
        this.gameLabsCurrentPage = 1;
        this.gameLabsLoading = false;
        this.isLoading = false;
        this.getGameLabsPaginated();
      }

      if (index === 6) {
        this.onboardingLoading = false;
        this.isLoading = false;
        this.getOnboardingDetail();
      }

      if (index === 7) {
        this.agentsCurrentPage = 1;
        this.agentsLoading = false;
        this.isLoading = false;
        this.getAgentsPaginated();
      }
    },
    showEditModal() {
      this.editModalVisible = true;
    },
    hideEditModal() {
      this.editModalVisible = false;
    },
    showDeleteModal() {
      this.deleteModalVisible = true;
      event.stopPropagation();
    },
    hideAssignModal() {
      this.assignmentModal = false;
    },
    handleDeleteModalClose() {
      this.deleteModalVisible = false;
    },
    openAssignModal() {
      this.assignmentModal = true;
    },
    openAssignTestModal() {
      this.assignTestModal = true;
    },
    hideAssignTestModal() {
      this.assignTestModal = false;
    },
    hideShowUserModal() {
      this.addUserModal = false;
    },

    showAssignGameLabsModal() {
      this.assignGameLabsModalVisible = true;
    },
    hideAssignGameLabsModal() {
      this.assignGameLabsModalVisible = false;
    },

    showAssignAgentModal() {
      this.assignAgentModalVisible = true;
    },
    hideAssignAgentModal() {
      this.assignAgentModalVisible = false;
    },

    getAgentName(name) {
      if (!name) return '-';
      if (typeof name === 'string') return name;
      const locale = localStorage.getItem('userLanguage') || localStorage.getItem('language') || 'tr';
      return name[locale] || name['tr'] || name['en'] || Object.values(name)[0] || '-';
    },

    async getAgentsPaginated() {
      this.agentsLoading = true;
      try {
        const response = await agentService.getAgentsByAssignment({
          companyId: this.details.companyId,
          userId: this.details.userId,
          type: 'user',
          page: this.agentsCurrentPage,
          limit: this.agentsPerPage,
        });

        if (response?.data?.success) {
          this.agentList = response.data.agents || [];
          this.totalAgents = response.data.pagination?.totalItems || 0;
        } else {
          this.agentList = [];
          this.totalAgents = 0;
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.agentList = [];
        this.totalAgents = 0;
      }
      this.agentsLoading = false;
    },

    handleAgentsPage(page) {
      if (this.agentsLoading) return;
      this.agentsCurrentPage = page;
      this.getAgentsPaginated();
    },

    getDetails() {
      const routeParams = this.$route.params;
      this.isLoading = true;

      userService.getUserDetails(routeParams.id).then((response) => {
        this.details = response.data.data;

        if (this.tabIndex === 1) {
          this.getProgramsPaginated();
        } else {
          this.totalPrograms = this.details.programs?.length || 0;
        }

        if (this.tabIndex === 2) {
          this.getTestsPaginated();
        } else {
          this.totalTests = this.details.tests?.length || 0;
        }

        if (this.tabIndex === 5) {
          this.getGameLabsPaginated();
        } else {
          this.totalGameLabs = this.details.gameLabs?.length || 0;
        }

        this.isLoading = false;
      });
    },

    async getOnboardingDetail() {
      this.onboardingLoading = true;
      try {
        const response = await userService.getOnboardingDetail(this.details.userId);
        if (response?.data?.responseType === 'success' && response.data.data) {
          this.onboardingData = response.data.data;
        } else {
          this.onboardingData = null;
        }
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.onboardingData = null;
      }
      this.onboardingLoading = false;
    },

    async getProgramsPaginated() {
      this.programsLoading = true;
      try {
        const response = await programService.getUserPrograms(
            this.programsCurrentPage,
            this.details.userId,
            [],
            this.searchText || '',
            this.programsPerPage
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.programList = response.data.data.data.map(program => ({
            ...program,
            canDelete: (program.assignedType || program.assignType || 'user') === 'user'
          }));
          this.totalPrograms = response.data.data.total || 0;
        } else {
          this.programList = [];
          this.totalPrograms = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.programList = [];
        this.totalPrograms = 0;
      }
      this.programsLoading = false;
    },

    async getTestsPaginated() {
      this.testsLoading = true;
      try {
        const response = await testService.getUserTests(
            this.testsCurrentPage,
            this.details.userId,
            [],
            this.searchText || '',
            this.testsPerPage
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.testList = response.data.data.data.map(test => ({
            ...test,
            canDelete: (test.assignedType || test.assignType || 'user') === 'user'
          }));
          this.totalTests = response.data.data.total || 0;
        } else {
          this.testList = [];
          this.totalTests = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.testList = [];
        this.totalTests = 0;
      }
      this.testsLoading = false;
    },

    async getGameLabsPaginated() {
      this.gameLabsLoading = true;
      try {
        const response = await gameLabService.getUserGameLabs(
            this.gameLabsCurrentPage,
            this.details.userId,
            [],
            this.searchText || '',
            this.gameLabsPerPage
        );


        if (response?.data?.responseType === 'success' && response.data.data) {
          this.gameLabsList = response.data.data.data.map(item => ({
            ...item,
            remaining: item.remaining || { isPast: 0, time: '' },
            canDelete: (item.assignedType || item.assignType || 'user') === 'user'
          }));
          this.totalGameLabs = response.data.data.total || 0;
        } else {
          this.gameLabsList = [];
          this.totalGameLabs = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.gameLabsList = [];
        this.totalGameLabs = 0;
      }
      this.gameLabsLoading = false;
    },

    handleProgramsPage(page) {
      if (this.programsLoading) return;
      this.programsCurrentPage = page;
      this.getProgramsPaginated();
    },

    handleTestsPage(page) {
      if (this.testsLoading) return;
      this.testsCurrentPage = page;
      this.getTestsPaginated();
    },

    handleGameLabsPage(page) {
      if (this.gameLabsLoading) return;
      this.gameLabsCurrentPage = page;
      this.getGameLabsPaginated();
    },

    formatDate(dateStr) {
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return "-";
      }
    },

    formatRemainingTime(timeObj) {
      if (typeof timeObj === 'string') {
        return timeObj
            .replace('yıl', this.$t('year'))
            .replace('ay', this.$t('month'))
            .replace('gün', this.$t('day'));
      }

      let result = '';
      if (timeObj.years) {
        result += `${timeObj.years} ${this.$t(timeObj.years > 1 ? 'years' : 'year')}`;
      }
      if (timeObj.months) {
        if (result) result += ', ';
        result += `${timeObj.months} ${this.$t(timeObj.months > 1 ? 'months' : 'month')}`;
      }
      if (timeObj.days) {
        if (result) result += ', ';
        result += `${timeObj.days} ${this.$t(timeObj.days > 1 ? 'days' : 'day')}`;
      }
      return result;
    },
    isValidDate(date) {
      return date && !isNaN(new Date(date).getTime());
    },

    handleDelete() {
      this.deleteModalVisible = false;

      userService.deleteUser(this.details.userId).then(() => {
        this.$router.push('/users');
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('user_delete_error'),
          type: 'error',
        });
      })
    },

    goToTest(testId) {
      this.$router.push(`/test/${testId}`);
    },

    goToGameLab(gameLabId) {
      this.$router.push(`/game/${gameLabId}`);
    },

    handleProgramSelect(programId) {
      const program = this.programList.find(p => p.programId === programId);

      if (program && !program.canDelete) {
        const assignedType = program.assignedType || program.assignType || 'unknown';

        let messageKey = '';
        if (assignedType === 'company') {
          messageKey = 'program_assigned_from_company';
        } else if (assignedType === 'department') {
          messageKey = 'program_assigned_from_department';
        } else {
          messageKey = 'program_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: assignedType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedPrograms.includes(programId)) {
        this.selectedPrograms = this.selectedPrograms.filter(id => id !== programId);
      } else {
        this.selectedPrograms.push(programId);
      }
    },

    isProgramSelected(programId) {
      return this.selectedPrograms.includes(programId);
    },

    handleTestSelect(testId) {
      const test = this.testList.find(t => t.testId === testId);

      if (test && !test.canDelete) {
        const assignedType = test.assignedType || test.assignType || 'unknown';

        let messageKey = '';
        if (assignedType === 'company') {
          messageKey = 'test_assigned_from_company';
        } else if (assignedType === 'department') {
          messageKey = 'test_assigned_from_department';
        } else {
          messageKey = 'test_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: assignedType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedTests.includes(testId)) {
        this.selectedTests = this.selectedTests.filter(id => id !== testId);
      } else {
        this.selectedTests.push(testId);
      }
    },

    isTestSelected(testId) {
      return this.selectedTests.includes(testId);
    },

    handleGameLabSelect(gameLabId) {
      const gameLab = this.gameLabsList.find(g => g.gameLabsId === gameLabId);

      if (gameLab && !gameLab.canDelete) {
        const assignedType = gameLab.assignedType || gameLab.assignType || 'unknown';

        let messageKey = '';
        if (assignedType === 'company') {
          messageKey = 'gamelab_assigned_from_company';
        } else if (assignedType === 'department') {
          messageKey = 'gamelab_assigned_from_department';
        } else {
          messageKey = 'gamelab_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: assignedType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedGameLabs.includes(gameLabId)) {
        this.selectedGameLabs = this.selectedGameLabs.filter(id => id !== gameLabId);
      } else {
        this.selectedGameLabs.push(gameLabId);
      }
    },

    isGameLabSelected(gameLabId) {
      return this.selectedGameLabs.includes(gameLabId);
    },

    handleMultipleProgramDelete() {
      if (this.selectedPrograms.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_programs_to_delete')
        });
        return;
      }

      if (this.deleteProgramSure) {
        clearTimeout(this.deleteProgramTimer);

        const deleteData = {
          programIds: this.selectedPrograms,
          userId: this.details.userId
        };

        programService.deleteProgramAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('program_assignments_deleted', { count: this.selectedPrograms.length })
          });

          this.selectedPrograms = [];
          this.deleteProgramSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
          this.deleteProgramSure = false;
        });
      } else {
        this.deleteProgramSure = true;
        this.deleteProgramTimer = setTimeout(() => {
          this.deleteProgramSure = false;
        }, 3000);
      }
    },

    handleMultipleTestDelete() {
      if (this.selectedTests.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_tests_to_delete')
        });
        return;
      }

      if (this.deleteTestSure) {
        clearTimeout(this.deleteTestTimer);

        const deleteData = {
          testIds: this.selectedTests,
          userId: this.details.userId
        };

        testService.deleteTestAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('test_assignments_deleted', { count: this.selectedTests.length })
          });

          this.selectedTests = [];
          this.deleteTestSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
          this.deleteTestSure = false;
        });
      } else {
        this.deleteTestSure = true;
        this.deleteTestTimer = setTimeout(() => {
          this.deleteTestSure = false;
        }, 3000);
      }
    },

    handleMultipleGameLabDelete() {
      if (this.selectedGameLabs.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_gamelabs_to_delete')
        });
        return;
      }

      if (this.deleteGameLabSure) {
        clearTimeout(this.deleteGameLabTimer);

        const deleteData = {
          gameLabsIds: this.selectedGameLabs,
          userId: this.details.userId
        };

        gameLabService.deleteGameLabAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('gamelab_assignments_deleted', { count: this.selectedGameLabs.length })
          });

          this.selectedGameLabs = [];
          this.deleteGameLabSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
          this.deleteGameLabSure = false;
        });
      } else {
        this.deleteGameLabSure = true;
        this.deleteGameLabTimer = setTimeout(() => {
          this.deleteGameLabSure = false;
        }, 3000);
      }
    },

    beforeUnmount() {
      if (this.deleteProgramTimer) {
        clearTimeout(this.deleteProgramTimer);
      }
      if (this.deleteTestTimer) {
        clearTimeout(this.deleteTestTimer);
      }
      if (this.deleteGameLabTimer) {
        clearTimeout(this.deleteGameLabTimer);
      }
    },

    handleTestCardClick(test) {
      const assignedType = test.assignedType || test.assignType || 'user';
      if (assignedType !== 'user') {
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'company') {
          messageKey = 'test_assigned_from_company';
        } else if (assignedType === 'department') {
          messageKey = 'test_assigned_from_department';
        } else {
          messageKey = 'test_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }
    },

    handleGameLabCardClick(gameLab) {
      const assignedType = gameLab.assignedType || gameLab.assignType || 'user';

      if (assignedType !== 'user') {
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'company') {
          messageKey = 'gamelab_assigned_from_company';
        } else if (assignedType === 'department') {
          messageKey = 'gamelab_assigned_from_department';
        } else {
          messageKey = 'gamelab_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }
    },

  }
}
</script>

<style scoped>
.tab-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-direction: row;
}
.main-content {
  padding: 25px;
}
.tab-panel-row {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #F2F4F7;
  border-bottom: 1px solid #F2F4F7;
  padding: 10px 0;
  margin-bottom: 20px;
}
.tab-button {
  padding: 6px 10px;
  border-radius: 10px;
  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;
}

.tab-button span {
  font-family: 'euclid_medium', sans-serif;
  font-size: 12px;
  color: #344054;
}
.tab-button.active, .tab-button:hover {
  background-color: #F2F4F7;
}
h3 {
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
  padding: 0;
  margin-top: 20px;
  margin-bottom: 20px;
}
.company-details-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 300px;
}
.company-detail-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
}
.company-detail-icon {
  background-color: #F5F5F5;
  border-radius: 16px;
  height: 44px;
  width: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.company-detail-icon img {
  cursor: default;
  width: 20px;
  height: 20px;
}
.detail-title {
  font-family: euclid_medium, sans-serif;
  display: block;
  color: #667085;
  font-size: 12px;
}
.detail-text {
  font-family: euclid_medium, sans-serif;
  display: block;
  color: #101828;
  font-size: 14px;
}
.company-detail-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.company-details {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.programs-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.program-page-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bottom-placement-pagination {
  display: flex;
  justify-content: center;
}

.tests-grid {
  width: 100%;
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.test-card-wrapper,
.gamelab-card-wrapper {
  cursor: pointer;
}

.test-card {
  position: relative;
  width: 100%;
  border: 1px solid #EAECF0;
  background-color: #FCFCFD;
  border-radius: 20px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.2s ease;
}

.test-card:hover {
  box-shadow: 0 4px 20px rgba(16, 24, 40, 0.1);
  transform: translateY(-2px);
}

.test-card.selected {
  border-color: #3DA33E;
  background-color: #f8fff8;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(61, 163, 62, 0.15);
}

.checkbox-wrapper {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.checkbox-wrapper:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: scale(1.1);
}

.checkbox-icon {
  width: 18px;
  height: 18px;
}

.status-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #040C2D;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 10px;
  display: flex;
  gap: 5px;
  align-items: center;
}

.action-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-tag.Draft {
  background-color: #FAD9C6;
}

.status-tag.Published {
  background-color: #DDFBDE;
}

.status-dot.Draft {
  background-color: #F09460;
}

.status-dot.Published {
  background-color: #3DA33E;
}

.test-date {
  font-family: euclid_medium, sans-serif;
  font-size: 10px;
  color: #667085;
  margin-top: 30px;
}

.test-title {
  font-family: euclid_medium, sans-serif;
  font-size: 24px;
  color: #040C2D;
  margin-bottom: 30px;
}

.info-row {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.info-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 10px;
  color: #040C2D;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 12px;
  display: flex;
  gap: 5px;
  align-items: center;
  background-color: #F2F4F7;
}

.test-info-text {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #667085;
}

.test-info-text.red {
  color: #ff2121;
}

.test-info-text.yellow {
  color: #f1c40f;
}

.test-info-text.green {
  color: #088701;
}

.dot {
  height: 4px;
  width: 4px;
  border-radius: 50%;
  background-color: #d0d5dd;
}

.test-info {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  margin-bottom: 30px;
}

.test-info-icon {
  width: 24px;
  height: 24px;
}

.test-time-row {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
}

.gameLabs-grid {
  width: 100%;
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.delete-button {
  padding: 8px 16px;
  border: 1px solid #ff4757;
  background-color: transparent;
  color: #ff4757;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  transition: all 0.2s ease;
}

.delete-button:hover {
  background-color: #ff4757;
  color: white;
}

.delete-button.sure {
  background-color: #ff4757;
  color: white;
  animation: pulse 1s infinite;
}

.delete-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.delete-button:disabled:hover {
  background-color: transparent;
  color: #ff4757;
}

.no-tests-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.no-tests-image {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
}

.no-tests-text {
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
  margin-bottom: 8px;
}

.no-tests-description {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #667085;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@media (max-width: 1024px) {
  .programs-row {
    grid-template-columns: 1fr 1fr;
  }

  .tests-grid,
  .gameLabs-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .programs-row {
    grid-template-columns: 1fr;
  }

  .tests-grid,
  .gameLabs-grid,
  .agents-grid {
    grid-template-columns: 1fr;
  }
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.agent-card {
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agent-card-top {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
}

.agent-card-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.agent-card-avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #4A68F8;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: euclid_semi_bold, sans-serif;
  font-size: 16px;
}

.agent-card-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.agent-card-name {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-card-desc {
  font-size: 12px;
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-card-bottom {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.agent-card-date {
  font-size: 12px;
  color: #98A2B3;
}
</style>
